import { format } from "date-fns";
import { Clock, Camera, Eye, MapPin, X } from "lucide-react";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDeviceViewCount } from "@/hooks/use-device-view-count";
import { getImageFreshness } from "@/utils/timeFreshnessUtils";
import { MiniMapDialog } from "./MiniMapDialog";

interface PublicDeviceDetailModalProps {
  device: {
    device_id: string;
    device_name: string;
    device_description?: string | null;
    device_last_updated: string;
    lat_wgs84: number | null;
    long_wgs84: number | null;
    past_images?: any[];
    imageKit?: {
      thumbnailUrl?: string;
      fullUrl?: string;
    };
  } | null;
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PublicDeviceDetailModal = ({ 
  device, 
  organizationId, 
  open, 
  onOpenChange 
}: PublicDeviceDetailModalProps) => {
  const { data: viewCount = 0 } = useDeviceViewCount(device?.device_id || '');

  if (!device) return null;

  const lastUpdatedDate = device.device_last_updated ? new Date(device.device_last_updated) : null;
  const lastUpdatedText = lastUpdatedDate && !isNaN(lastUpdatedDate.getTime())
    ? format(lastUpdatedDate, "MMM d, yyyy 'at' h:mm a")
    : 'Unknown';

  const latestImage = device.past_images && device.past_images.length > 0 
    ? device.past_images[device.past_images.length - 1] as any
    : null;

  const thumbnailUrl = device.imageKit?.thumbnailUrl || latestImage?.url;
  const fullImageUrl = device.imageKit?.fullUrl || latestImage?.url || thumbnailUrl;
  const freshness = getImageFreshness(device.device_last_updated);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="showcase-dialog max-w-4xl max-h-[90vh] overflow-y-auto p-0">
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-4 z-50 bg-background/80 backdrop-blur-sm hover:bg-background"
          onClick={() => onOpenChange(false)}
        >
          <X className="h-4 w-4" />
        </Button>

        <div className="space-y-4 p-6">
          {/* Image Section */}
          <div className="relative">
            {thumbnailUrl ? (
              <div className="relative">
                <Zoom zoomMargin={40} classDialog="z-[100]">
                  <img
                    src={fullImageUrl}
                    alt={device.device_name}
                    className="w-full max-h-[60vh] object-contain rounded-lg cursor-zoom-in"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = "/placeholder.svg";
                    }}
                  />
                </Zoom>
                
                {/* Freshness Badge */}
                <div className="absolute top-3 right-3 z-40">
                  <Badge 
                    variant={freshness.variant}
                    className={`${freshness.colorClass} shadow-md text-destructive-foreground border-0`}
                  >
                    {freshness.text}
                  </Badge>
                </div>

                {/* View Count Badge */}
                {viewCount > 0 && (
                  <div className="absolute bottom-3 right-3 z-40">
                    <Badge variant="secondary" className="showcase-image-badge bg-foreground/70 text-background border-0 shadow-md">
                      <Eye className="h-3 w-3 mr-1" />
                      {viewCount.toLocaleString()}
                    </Badge>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-96 bg-muted rounded-lg flex flex-col items-center justify-center gap-3">
                <Camera className="h-16 w-16 text-muted-foreground/40" />
                <span className="text-sm text-muted-foreground">No image available</span>
              </div>
            )}
          </div>

          {/* Device Information */}
          <div className="space-y-4">
            <div>
              <h2 className="showcase-heading text-2xl font-bold text-foreground">
                {device.device_name || "Unnamed Device"}
              </h2>
            </div>

            {device.device_description && (
              <div className="text-sm text-muted-foreground">
                {device.device_description}
              </div>
            )}

            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{lastUpdatedText}</span>
              </div>

              {device.lat_wgs84 && device.long_wgs84 && (
                <MiniMapDialog
                  latitude={device.lat_wgs84}
                  longitude={device.long_wgs84}
                  deviceName={device.device_name}
                  thumbnailUrl={thumbnailUrl}
                />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
